export interface SubscriptionHandler<T = any> {
  name: string;
  selector: string;
  events: Map<T, SubscriptionEventHandler[]>,
  onError: (error: Error) => void;
}

type NextCallback = void | (() => any);

export type SubscriptionEventHandler = (data: any) => NextCallback;

export type Unsubscribe = () => void;

// Handle incoming subscription data and allow each to resolve.
// Underlying models will already have been merged with the
// incoming data when handlers are called.

// Handlers can be promises and is resolved/called sequentially
export default <T extends (...args: any) => Unsubscribe>(
  subscriptionFn: T,
  variables: Parameters<T>[0],
  subscriptionHandler: SubscriptionHandler,
) => subscriptionFn(
  variables,
  subscriptionHandler.selector,
  async (data: any) => {
    if (data != null) {
      const { __typename: type } = data;
      const registeredHandlers = subscriptionHandler.events.get(type);

      if (registeredHandlers != null && registeredHandlers?.length > 0) {
        const next: NextCallback[] = [];
        registeredHandlers.forEach((fn) => next.push(fn(data)));

        next.forEach((fn) => {
          if (fn != null) {
            fn();
          }
        });
      }
    }
  },
  subscriptionHandler.onError,
);

export const createSubscriptionHandler = <T>(handler: {
  name: string,
  selector: string,
  events: Map<T, SubscriptionEventHandler[]>,
  onError: (error: Error) => void,
}) => handler;
